// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@eisinc/MobiusOverrides/DatePicker";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@eisinc/Overrides/Components/AddressInfoDisplay";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    location: WarehouseModel;
    onEdit: () => void;
    extendedStyles?: PickUpLocationAddressInfoDisplayStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId, requestedPickupDate } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const settingsCollection = getSettingsCollection(state);
    const session = getSession(state);

    const cart = cartState.value;

    return {
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        // XNG-Changes
        cart,
        cartLines: cart?.cartLines || [],
        requestedPickupDate,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        cartSettings: settingsCollection.cartSettings,
        showPickUpDate:
            settingsCollection.accountSettings.enableWarehousePickup &&
            settingsCollection.cartSettings.enableRequestPickUpDate &&
            session.fulfillmentMethod === FulfillmentMethod.PickUp,
    };
};

const mapDispatchToProps = {
    setRequestedPickUpDate,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PickUpLocationAddressInfoDisplayStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    addressGridItem?: GridItemProps;
    locationNameText?: TypographyPresentationProps;
    address?: AddressInfoDisplayStyles;
    // XNG-Changes
    changeLink?: LinkPresentationProps;
    directionsLink?: LinkPresentationProps;
    changeFulfilmentLink?: LinkPresentationProps;
    findLocationModal?: FindLocationModalStyles;
    pickupInstructionsModal?: ModalPresentationProps;
    pickUpDateContainer?: InjectableCss;
    requestPickUpDateContainer?: InjectableCss;
    pickUpDatePicker?: DatePickerPresentationProps;
    messagesContainer?: InjectableCss;
    message?: TypographyPresentationProps;
}

export const pickUpLocationAddressInfoDisplayStyles: PickUpLocationAddressInfoDisplayStyles = {
    container: { gap: 0 },
    headingGridItem: { width: 12 },
    headingText: {
        weight: 600,
        css: css`
            margin: 0 1rem 0 0;
        `,
    },
    addressGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
};

const PickUpLocationAddressInfoDisplay = ({
    location,
    onEdit,
    extendedStyles,
    pickUpWarehouse,
    cart,
    cartLines,
    cartUrl,
    requestedPickupDate,
    setRequestedPickUpDate,
    cartSettings,
    showPickUpDate,
}: Props) => {
    const styles = mergeToNew(pickUpLocationAddressInfoDisplayStyles, extendedStyles);

    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    const [isPickupInstructionsModalOpen, setIsPickupInstructionsModalOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    const toasterContext = React.useContext(ToasterContext);

    // XNG-Changes
    const [isToday, setIsToday] = useState(false);

    const handleOpenFindLocation = () => {
        setIsFindLocationOpen(true);
    };

    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);

    const updatePickUpSelectedWarehouse = async (pickUpWarehouse: WarehouseModel) => {
        await updatePickUpWarehouse({ pickUpWarehouse });
    };

    const handleWarehouseSelected = (pickUpWarehouse: WarehouseModel) => {
        setIsLoading(true);
        updatePickUpSelectedWarehouse(pickUpWarehouse).then(result => {
            toasterContext.addToast({
                body: translate("Pick Up Address Updated"),
                messageType: "success",
            });
            setIsFindLocationOpen(false);
            setIsLoading(false);
        });
    };

    // XNG-Changes
    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        const isToday = !!(
            selectedDay &&
            selectedDay.getDate() === new Date().getDate() &&
            selectedDay.getMonth() === new Date().getMonth()
        );

        setIsToday(isToday);

        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
    };

    // date fields validation
    const tomorrow = new Date();
    tomorrow.setDate(new Date().getDate() + 1);

    const hasNothing2Pickup = cartLines.every(line => {
        const qty = parseInt(line.properties["totalInSelectedPlant"], 10);
        return qty === 0 || !("totalInSelectedPlant" in (line.properties ?? {})) || !line.qtyOrdered;
    });

    const after3PM = isToday && new Date().getHours() > 15;

    return (
        <StyledWrapper {...styles.container} data-test-selector="pickUpLocationAddressInfoDisplay_container">
            <StyledWrapper {...styles.headingGridItem}>
                <Typography {...styles.headingText}>{translate("Pick Up Location")}</Typography>
                <Link {...styles.editLink} onClick={onEdit}>
                    {translate("Edit")}
                    <VisuallyHidden>{translate("Pick Up Location")}</VisuallyHidden>
                </Link>
            </StyledWrapper>
            <StyledWrapper {...styles.addressGridItem}>
                <Typography {...styles.locationNameText}>{location.description || location.name}</Typography>
                <AddressInfoDisplay {...location} extendedStyles={styles.address} />
                {pickUpWarehouse?.hours && (
                    <Link
                        onClick={() => setIsPickupInstructionsModalOpen(true)}
                        {...styles.changeLink}
                        data-test-selector="checkout_pickUpInstructions"
                    >
                        {translate("Pickup Instructions")}
                    </Link>
                )}
                <Link
                    href={`http://maps.google.com/maps?daddr=${location.address1} ${location.address2}, ${location.city}, ${location.state} ${location.postalCode}`}
                    {...styles.directionsLink}
                    target="_blank"
                    data-test-selector="checkout_pickUpLocation_directions"
                >
                    {translate("Get Directions")}
                </Link>
                {cartUrl && (
                    <Link href={cartUrl} {...styles.changeFulfilmentLink} data-test-selector="checkout_changeMethod">
                        {translate("Click here to change the fulfilment method for this order")}
                    </Link>
                )}
                {cart && (
                    <StyledWrapper
                        {...styles.pickUpDateContainer}
                        data-test-selector="checkoutPickUpRequestedDeliveryDate"
                    >
                        {showPickUpDate && (
                            <StyledWrapper {...styles.requestPickUpDateContainer}>
                                <DatePicker
                                    label={`${translate("Request Pick Up Date")}`}
                                    selectedDay={cart.requestedPickupDateDisplay! || requestedPickupDate}
                                    dateTimePickerProps={{
                                        minDate: hasNothing2Pickup ? tomorrow : new Date(),
                                        maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                        ...styles.pickUpDatePicker?.dateTimePickerProps,
                                    }}
                                    onDayChange={handleRequestPickUpDateChanged}
                                    optional={true}
                                    monthAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                        "Enter month, format M M",
                                    )}`}
                                    dayAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                        "Enter day, format D D",
                                    )}`}
                                    yearAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                        "Enter year, format Y Y Y Y",
                                    )}`}
                                    {...styles.pickUpDatePicker}
                                />
                                <StyledWrapper {...styles.messagesContainer}>
                                    <Typography {...styles.message}>
                                        {siteMessage("Checkout_RequestedPickupDateInformation")}
                                    </Typography>
                                    {hasNothing2Pickup && (
                                        <Typography {...styles.message}>
                                            {siteMessage("Custom_Checkout_RequestedPickupHasNothingToPickup")}
                                        </Typography>
                                    )}
                                    {after3PM && (
                                        <Typography {...styles.message}>
                                            {siteMessage("Custom_Checkout_RequestedPickupAfter3Message")}
                                        </Typography>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                )}
                <FindLocationModal
                    modalIsOpen={isFindLocationOpen}
                    onWarehouseSelected={handleWarehouseSelected}
                    onModalClose={handleFindLocationModalClose}
                    extendedStyles={styles.findLocationModal}
                    {...({ isWarehouseSelectedLoading: isLoading } as any)}
                />
            </StyledWrapper>
            <Modal
                headline={translate("Hours")}
                {...styles.pickupInstructionsModal}
                isOpen={isPickupInstructionsModalOpen}
                handleClose={() => setIsPickupInstructionsModalOpen(false)}
            >
                {pickUpWarehouse?.hours}
            </Modal>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(PickUpLocationAddressInfoDisplay);
